<template>
  <div class="logs-header" data-testid="logs-panel-header">
    <h2 class="logs-header__title">Logs</h2>

    <div class="logs-header__actions">
      <button
        type="button"
        class="logs-header__icon-btn logs-header__autoscroll"
        :class="{ 'logs-header__autoscroll--active': autoScroll }"
        :title="autoScroll ? 'Auto-scroll is on. Click to disable.' : 'Auto-scroll is off. Click to enable.'"
        :aria-label="autoScroll ? 'Disable auto-scroll' : 'Enable auto-scroll'"
        :aria-pressed="autoScroll"
        data-testid="logs-autoscroll-toggle"
        @click="$emit('toggleAutoScroll')"
      >
        <!-- Stacked chevrons (up + down) — distinct from the single chevronDown
             used elsewhere in the viewer for folding panels. -->
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.25"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <polyline points="7 9 12 4 17 9" />
          <polyline points="7 15 12 20 17 15" />
        </svg>
      </button>

      <a
        v-if="insightsURL"
        :href="insightsURL"
        target="_blank"
        rel="noopener noreferrer"
        class="logs-header__icon-btn"
        title="Download logs"
        aria-label="Download logs"
        data-testid="logs-download"
      >
        <Icon name="general.downloadFile" size="sm" />
      </a>

      <button
        v-if="isCurrentItemOwner"
        type="button"
        class="logs-header__icon-btn"
        title="Create a ticket"
        aria-label="Create a ticket"
        data-testid="logs-create-ticket"
        @click="$emit('createTicket')"
      >
        <Icon name="general.bug" size="sm" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { Icon } from '@/storybook';
import { useArtifactsStore } from '@/modules/viewer/store/useArtifactsStore';
import { useUser } from '@/modules/viewer/composables/useUser';

defineProps<{
  autoScroll: boolean;
}>();

defineEmits<{
  createTicket: [];
  toggleAutoScroll: [];
}>();

const { insightsURL } = storeToRefs(useArtifactsStore());
// Only owners can create a support ticket (the action opens the share modal's
// Export tab, which has owner-only surfaces). Same-org team members still see
// everything else — Auto-scroll, Download logs, the log entries themselves.
const { isCurrentItemOwner } = useUser();
</script>

<style scoped>
.logs-header {
  @apply flex items-center justify-between gap-3 px-4 sm:px-5 py-3;
}

.logs-header__title {
  @apply text-base sm:text-lg font-semibold text-zinc-900 dark:text-zinc-100 leading-tight min-w-0;
}

.logs-header__actions {
  @apply flex items-center gap-2 flex-shrink-0;
}

.logs-header__icon-btn {
  @apply inline-flex items-center justify-center w-9 h-9 rounded-full text-zinc-600 dark:text-zinc-400 no-underline cursor-pointer transition-colors;
  @apply hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-700 dark:hover:text-zinc-200;
}

.logs-header__autoscroll--active {
  @apply bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400;
}

.logs-header__autoscroll--active:hover {
  @apply bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300;
}
</style>
